import { useId } from 'react';
import { SocialNetworkType } from '@repo/types';

// Native brand glyphs (simple-icons paths, 24×24). Most are single-path marks
// rendered with `fill="currentColor"`, so their brand colour is driven by CSS.
// Two follow their platforms' official multi-colour marks instead of a flat
// colour: Instagram is filled with its brand gradient, and TikTok is drawn as
// the note plus its cyan/red "glitch" offsets (see the dedicated components
// below). On the dark footer each glyph sits on a white circular chip so the
// brand colours — including the black marks — stay legible (SC-1146 revisited).
const GLYPH_PATHS: Record<SocialNetworkType, string> = {
  [SocialNetworkType.Facebook]:
    'M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.49 0-1.96.93-1.96 1.89v2.25h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z',
  [SocialNetworkType.Twitter]:
    'M18.9 1.2h3.7l-8 9.1L24 22.8h-7.4l-5.8-7.6-6.6 7.6H.5l8.6-9.8L0 1.2h7.6l5.2 6.9zM17.6 20.6h2L6.5 3.3H4.3z',
  [SocialNetworkType.Instagram]:
    'M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.43-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.9 5.9 0 0 0-2.13 1.38A5.9 5.9 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13a5.9 5.9 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 0 0 2.13-1.38 5.9 5.9 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 0 0-1.38-2.13A5.9 5.9 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zM12 16a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm6.41-10.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44z',
  [SocialNetworkType.YouTube]:
    'M23.5 6.2a3 3 0 0 0-2.12-2.13C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.52A3 3 0 0 0 .5 6.2 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.8 3 3 0 0 0 2.12 2.13c1.88.52 9.38.52 9.38.52s7.5 0 9.38-.52a3 3 0 0 0 2.12-2.13A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.8zM9.55 15.57V8.43L15.82 12z',
  [SocialNetworkType.TikTok]:
    'M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  [SocialNetworkType.Threads]:
    'M12.19 0h.63c2.54.06 4.6.9 6.1 2.5 1.4 1.48 2.18 3.54 2.43 6.03h-2.36c-.22-1.9-.83-3.38-1.8-4.4-1.04-1.1-2.55-1.69-4.43-1.74h-.06c-1.9.04-3.36.66-4.36 1.85-.94 1.12-1.43 2.74-1.46 4.83v.18c.03 2.09.52 3.71 1.46 4.83 1 1.19 2.46 1.81 4.36 1.85h.06c1.7-.04 2.84-.46 3.74-1.4.7-.74 1.2-1.78 1.43-2.92-.96-.43-2.13-.66-3.41-.62-1.6.05-2.82.5-3.6 1.32-.5.53-.74 1.18-.7 1.83.04.74.42 1.32 1.06 1.62.55.27 1.23.32 1.86.16.9-.24 1.5-.93 1.72-1.92.1-.46.13-.97.1-1.5.74.15 1.42.4 2.02.74-.04.69-.16 1.36-.4 1.98-.5 1.34-1.55 2.32-2.96 2.7-1.18.31-2.43.18-3.49-.36-1.32-.67-2.13-1.93-2.22-3.46-.08-1.34.41-2.6 1.38-3.55 1.18-1.16 2.9-1.8 4.97-1.86 1.13-.03 2.2.1 3.18.4-.07-.6-.2-1.16-.4-1.66-.34-.86-.9-1.5-1.65-1.9-.78-.42-1.74-.6-2.84-.55-.84.04-1.55.24-2.1.6l-1.2-1.74c.84-.55 1.94-.86 3.2-.92z',
  [SocialNetworkType.WhatsApp]:
    'M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.06 2.87 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.62.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35zM12.05 21.8h-.02a9.78 9.78 0 0 1-4.98-1.36l-.36-.21-3.7.97.99-3.61-.23-.37a9.76 9.76 0 0 1-1.5-5.2c0-5.4 4.4-9.8 9.8-9.8 2.62 0 5.08 1.02 6.93 2.88a9.74 9.74 0 0 1 2.87 6.93c0 5.4-4.39 9.79-9.79 9.79zM20.5 3.5A11.7 11.7 0 0 0 12.05 0C5.6 0 .35 5.25.34 11.7c0 2.06.54 4.07 1.56 5.85L.24 24l6.6-1.73a11.7 11.7 0 0 0 5.2 1.24h.01c6.45 0 11.7-5.25 11.7-11.7 0-3.13-1.22-6.07-3.43-8.28z',
  [SocialNetworkType.Bluesky]:
    'M12 10.8C10.9 8.66 7.88 4.78 5.07 2.79 2.38.88 1.35 1.21.67 1.51.03 1.79 0 2.68 0 3.2c0 .53.29 4.34.48 4.98.63 2.11 2.86 2.83 4.92 2.6-3.02.45-5.7 1.55-2.18 5.47 3.86 3.99 5.3-.86 6.03-3.32.73 2.46 1.59 7.15 5.99 3.32 3.3-3.32.85-5.02-2.18-5.47 2.06.23 4.29-.49 4.92-2.6.19-.64.48-4.45.48-4.98 0-.52-.03-1.41-.67-1.69-.68-.3-1.71-.63-4.4 1.28C16.12 4.78 13.1 8.66 12 10.8z',
};

// slug used for the per-network brand-colour CSS hook (social-networks__glyph--x)
const NETWORK_SLUG: Record<SocialNetworkType, string> = {
  [SocialNetworkType.Facebook]: 'facebook',
  [SocialNetworkType.Twitter]: 'twitter',
  [SocialNetworkType.Instagram]: 'instagram',
  [SocialNetworkType.YouTube]: 'youtube',
  [SocialNetworkType.TikTok]: 'tiktok',
  [SocialNetworkType.Threads]: 'threads',
  [SocialNetworkType.WhatsApp]: 'whatsapp',
  [SocialNetworkType.Bluesky]: 'bluesky',
};

// Official Instagram gradient (warm yellow/orange → magenta → purple/blue),
// applied to the camera glyph — Instagram's mark is a gradient, not a flat
// colour. `useId` keeps the gradient id unique when several Instagram glyphs
// render on one page (e.g. footer + article); the `#e4405f` fallback keeps a
// solid brand colour if the gradient reference can't resolve.
function InstagramGlyph() {
  const gradientId = useId();
  return (
    <svg className="social-networks__glyph social-networks__glyph--instagram" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="24" x2="24" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#feda75" />
          <stop offset="0.25" stopColor="#fa7e1e" />
          <stop offset="0.5" stopColor="#d62976" />
          <stop offset="0.75" stopColor="#962fbf" />
          <stop offset="1" stopColor="#4f5bd5" />
        </linearGradient>
      </defs>
      <path d={GLYPH_PATHS[SocialNetworkType.Instagram]} fill={`url(#${gradientId}) #e4405f`} />
    </svg>
  );
}

// Official TikTok mark: the note plus its cyan (#25F4EE) and red (#FE2C55)
// "glitch" offsets. The note itself uses currentColor so it stays black on
// light surfaces and on the footer's white chips.
function TikTokGlyph() {
  const path = GLYPH_PATHS[SocialNetworkType.TikTok];
  return (
    <svg
      className="social-networks__glyph social-networks__glyph--tiktok"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d={path} fill="#25f4ee" transform="translate(-1 -1)" />
      <path d={path} fill="#fe2c55" transform="translate(1 1)" />
      <path d={path} />
    </svg>
  );
}

// Single-path brand marks: colour comes from the per-network CSS hook via
// `currentColor` (see social-networks.scss).
function DefaultGlyph({ network }: { network: SocialNetworkType }) {
  return (
    <svg
      className={`social-networks__glyph social-networks__glyph--${NETWORK_SLUG[network]}`}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d={GLYPH_PATHS[network]} />
    </svg>
  );
}

// GLYPH_PATHS / NETWORK_SLUG are exhaustive over SocialNetworkType, and callers
// only ever pass a validated network, so no missing-path guard is needed.
export function SocialGlyph({ network }: { network: SocialNetworkType }) {
  if (network === SocialNetworkType.Instagram) {
    return <InstagramGlyph />;
  }
  if (network === SocialNetworkType.TikTok) {
    return <TikTokGlyph />;
  }
  return <DefaultGlyph network={network} />;
}
